import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Reveal from '../../../shared/Reveal/Reveal';
import { submitVpsHostingRequest } from '../../../../api/vpsHostingRequestsApi';
import { phoneCodeOptions } from '../../../../data/countries';
import { validateVpsHostingRequestForm } from '../../../../utils/validateVpsHostingRequestForm';
import Select from '../../CreateAccount/Form/Select';
import '../../CreateAccount/Form/Form.css';
import './Form.css';

const SERVER_LOCATIONS = [
  { value: 'New York', label: 'New York' },
  { value: 'London', label: 'London' },
  { value: 'Frankfurt', label: 'Frankfurt' },
  { value: 'Singapore', label: 'Singapore' },
  { value: 'Tokyo', label: 'Tokyo' },
];

const SERVER_TYPES = [
  { value: 'Essential VPS', label: 'Essential VPS' },
  { value: 'Professional VPS', label: 'Professional VPS' },
  { value: 'Performance VPS', label: 'Performance VPS' },
];

const phoneOptions = phoneCodeOptions.map((option) => ({
  ...option,
  label: option.value,
}));

const getInitialServerType = (param) => {
  if (!param) return '';
  const match = SERVER_TYPES.find(
    (option) => option.value.toLowerCase() === param.toLowerCase()
  );
  return match?.value || '';
};

const FieldError = ({ message }) =>
  message ? <p className="create-account__field-error">{message}</p> : null;

const Form = () => {
  const [searchParams] = useSearchParams();
  const selectedServerType = searchParams.get('serverType');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [countryCode, setCountryCode] = useState('US');
  const [phone, setPhone] = useState('');
  const [serverLocation, setServerLocation] = useState('');
  const [serverType, setServerType] = useState(() =>
    getInitialServerType(selectedServerType)
  );
  const [message, setMessage] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    const nextType = getInitialServerType(selectedServerType);
    if (nextType) setServerType(nextType);
  }, [selectedServerType]);

  const inputClass = (field) =>
    `create-account__input${fieldErrors[field] ? ' create-account__input--error' : ''}`;

  const clearFieldError = (field) => {
    setFieldErrors((current) => {
      if (!current[field]) {
        return current;
      }

      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const getFormValues = () => ({
    firstName,
    lastName,
    email,
    countryCode,
    phone,
    serverLocation,
    serverType,
    message,
    agreed,
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError('');

    const values = getFormValues();
    const validation = validateVpsHostingRequestForm(values);

    if (!validation.isValid) {
      setFieldErrors(validation.errors);
      setFormError('Please fix the highlighted fields and try again.');
      return;
    }

    setFieldErrors({});
    setIsSubmitting(true);

    try {
      await submitVpsHostingRequest({
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        email: values.email.trim(),
        countryCode: values.countryCode.trim(),
        phone: values.phone.trim(),
        serverLocation: values.serverLocation,
        serverType: values.serverType,
        message: values.message.trim(),
        agreedToPrivacy: values.agreed,
      });

      setIsSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      if (error.errors?.length) {
        setFormError(error.errors.join(' '));
      } else {
        setFormError(error.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <section className="vps-hosting-request">
        <div className="vps-hosting-request__inner">
          <Reveal className="vps-hosting-request__intro">
            <h1 className="vps-hosting-request__title">Request received</h1>
            <p className="vps-hosting-request__support">
              Thank you for your VPS hosting request. Our team will review your details and
              follow up with the next steps.
            </p>
          </Reveal>
        </div>
      </section>
    );
  }

  return (
    <section className="vps-hosting-request">
      <div className="vps-hosting-request__inner">
        <Reveal className="vps-hosting-request__intro">
          <h1 className="vps-hosting-request__title">VPS Hosting Request</h1>
          <p className="vps-hosting-request__support">
            Tell us about your setup needs and our team will help provision a
            stable, secure VPS environment for your platform.
          </p>
        </Reveal>

        <Reveal className="vps-hosting-request__panel" delay={80}>
          <form
            className="vps-hosting-request__form create-account"
            onSubmit={handleSubmit}
            noValidate
          >
            {formError ? (
              <p className="create-account__form-error" role="alert">
                {formError}
              </p>
            ) : null}

            <div className="vps-hosting-request__row">
              <div className="create-account__field">
                <label className="create-account__label" htmlFor="vps-first-name">
                  First name
                </label>
                <input
                  id="vps-first-name"
                  className={inputClass('firstName')}
                  type="text"
                  name="firstName"
                  value={firstName}
                  onChange={(event) => {
                    setFirstName(event.target.value);
                    clearFieldError('firstName');
                  }}
                  placeholder="First name"
                  autoComplete="given-name"
                />
                <FieldError message={fieldErrors.firstName} />
              </div>

              <div className="create-account__field">
                <label className="create-account__label" htmlFor="vps-last-name">
                  Last name
                </label>
                <input
                  id="vps-last-name"
                  className={inputClass('lastName')}
                  type="text"
                  name="lastName"
                  value={lastName}
                  onChange={(event) => {
                    setLastName(event.target.value);
                    clearFieldError('lastName');
                  }}
                  placeholder="Last name"
                  autoComplete="family-name"
                />
                <FieldError message={fieldErrors.lastName} />
              </div>
            </div>

            <div className="vps-hosting-request__row">
              <div className="create-account__field">
                <label className="create-account__label" htmlFor="vps-email">
                  Email
                </label>
                <input
                  id="vps-email"
                  className={inputClass('email')}
                  type="email"
                  name="email"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    clearFieldError('email');
                  }}
                  placeholder="you@example.com"
                  autoComplete="email"
                />
                <FieldError message={fieldErrors.email} />
              </div>

              <div className="create-account__field">
                <label className="create-account__label" htmlFor="vps-phone">
                  Phone number
                </label>
                <div className="create-account__phone">
                  <Select
                    className="create-account__dropdown--phone"
                    name="countryCode"
                    options={phoneOptions}
                    value={countryCode}
                    onChange={(value) => {
                      setCountryCode(value);
                      clearFieldError('countryCode');
                    }}
                    searchable
                    searchPlaceholder="Search country"
                    aria-label="Country code"
                  />
                  <input
                    id="vps-phone"
                    className={`${inputClass('phone')} create-account__input--phone`}
                    type="tel"
                    name="phone"
                    value={phone}
                    onChange={(event) => {
                      setPhone(event.target.value);
                      clearFieldError('phone');
                    }}
                    placeholder="+1 (555) 000-0000"
                    autoComplete="tel"
                  />
                </div>
                <FieldError message={fieldErrors.countryCode || fieldErrors.phone} />
              </div>
            </div>

            <div className="vps-hosting-request__row">
              <div className="create-account__field">
                <label className="create-account__label">
                  Server location
                </label>
                <Select
                  name="serverLocation"
                  options={SERVER_LOCATIONS}
                  value={serverLocation}
                  onChange={(value) => {
                    setServerLocation(value);
                    clearFieldError('serverLocation');
                  }}
                  placeholder="Select"
                  aria-label="Server location"
                />
                <FieldError message={fieldErrors.serverLocation} />
              </div>

              <div className="create-account__field">
                <label className="create-account__label">
                  Server type
                </label>
                <Select
                  name="serverType"
                  options={SERVER_TYPES}
                  value={serverType}
                  onChange={(value) => {
                    setServerType(value);
                    clearFieldError('serverType');
                  }}
                  placeholder="Select"
                  aria-label="Server type"
                />
                <FieldError message={fieldErrors.serverType} />
              </div>
            </div>

            <div className="create-account__field">
              <label className="create-account__label" htmlFor="vps-message">
                Message
              </label>
              <textarea
                id="vps-message"
                className={`vps-hosting-request__textarea${
                  fieldErrors.message ? ' create-account__input--error' : ''
                }`}
                name="message"
                rows={5}
                value={message}
                onChange={(event) => {
                  setMessage(event.target.value);
                  clearFieldError('message');
                }}
                placeholder="Briefly describe what success looks like for you."
              />
              <FieldError message={fieldErrors.message} />
            </div>

            <label className="create-account__consent">
              <input
                className="create-account__checkbox"
                type="checkbox"
                name="privacyPolicy"
                checked={agreed}
                onChange={(event) => {
                  setAgreed(event.target.checked);
                  clearFieldError('privacyPolicy');
                }}
              />
              <span>
                You agree to our friendly{' '}
                <Link
                  to="/privacy"
                  className="create-account__consent-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  privacy policy
                </Link>
                .
              </span>
            </label>
            <FieldError message={fieldErrors.privacyPolicy} />

            <button
              type="submit"
              className="create-account__submit luxury-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting…' : 'Get started'}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default Form;
